import type { ChatStreamEvent } from "~/ai-chat/data-source/stream-chat-message";
import type { ChatToolCall, ChatTranscriptItem } from "~/ai-chat/model/chat-message";

type AppendMessage = (item: ChatTranscriptItem) => void;
type UpdateMessage = (id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => void;
type ToolCallEvent = Extract<ChatStreamEvent, { type: "toolCall" }>;

/**
 * One streaming run's assistant bubble. It is created on the first delta so an empty bubble never
 * flashes; later deltas append to it in place. Tool calls that start (and finish) before it exists are
 * held and land on it when it's created, or on an empty bubble if the run ends without text (see flush).
 */
export function createAssistantBubble(appendMessage: AppendMessage, updateMessage: UpdateMessage) {
  let id: string | null = null;
  const pendingToolCalls: ChatToolCall[] = [];
  const updateCalls = (update: (calls: ChatToolCall[]) => ChatToolCall[]) => {
    if (id === null) {
      pendingToolCalls.splice(0, pendingToolCalls.length, ...update(pendingToolCalls));
      return;
    }
    updateMessage(id, (item) =>
      item.kind === "message" ? { ...item, toolCalls: update(item.toolCalls ?? []) } : item,
    );
  };
  const create = (content: string) => {
    id = crypto.randomUUID();
    appendMessage({
      kind: "message",
      id,
      role: "assistant",
      content,
      createdAt: new Date(),
      ...(pendingToolCalls.length > 0 ? { toolCalls: pendingToolCalls.splice(0) } : {}),
    });
  };
  return {
    appendDelta(content: string) {
      if (id === null) {
        create(content);
        return;
      }
      updateMessage(id, (item) => (item.kind === "message" ? { ...item, content: item.content + content } : item));
    },
    recordToolCall(event: ToolCallEvent) {
      const { args } = event;
      if (event.status === "started") {
        if (args !== undefined) updateCalls((calls) => [...calls, { tool: event.name, args }]);
        return;
      }
      const { outcome } = event;
      if (outcome === undefined) return;
      // The latest unfinished start of this tool, so a sibling that already finished keeps its own outcome.
      updateCalls((calls) =>
        replaceLast(
          calls,
          (call) => call.tool === event.name && !call.outcome,
          (call) => ({ ...call, outcome }),
        ),
      );
    },
    // Tool calls ran but no text came (stopped, or paused for approval): give them an empty bubble so
    // they stay visible. A no-op once a bubble exists.
    flush() {
      if (id === null && pendingToolCalls.length > 0) create("");
    },
    // A gated call's start precedes its approval pause, but it hasn't run: it runs on the approved
    // resume. Drop the latest start of that tool (by name, so a sibling that did run is kept).
    dropGatedToolCall(tool: string) {
      updateCalls((calls) =>
        replaceLast(
          calls,
          (call) => call.tool === tool,
          () => null,
        ),
      );
    },
  };
}

function replaceLast(
  calls: ChatToolCall[],
  matches: (call: ChatToolCall) => boolean,
  replace: (call: ChatToolCall) => ChatToolCall | null,
): ChatToolCall[] {
  const index = calls.map(matches).lastIndexOf(true);
  if (index === -1) return calls;
  const replacement = replace(calls[index]);
  return [...calls.slice(0, index), ...(replacement ? [replacement] : []), ...calls.slice(index + 1)];
}
